import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef, useMemo } from "react";
import type { NotaFiscal } from "@/data/mock";
import { api } from "@/lib/api";

/** Foco e visibilidade disparam juntos ao voltar para a aba: um aviso só. */
const INTERVALO_MIN_AVISO_MS = 1000;

/**
 * As NFs não ficam mais em memória: cada tela busca o que mostra (lista
 * paginada, dashboard, detalhe). O contexto guarda só as ações e um contador
 * `versao` que avisa as telas abertas para recarregar.
 */
interface NFContextValue {
  /** Muda quando as NFs podem ter mudado fora da tela (volta à aba, reprocessamento). */
  versao: number;
  /** Pede às telas abertas que recarreguem. */
  refresh: () => void;
  addNota: (formData: FormData) => Promise<NotaFiscal>;
  aprovar: (id: string) => Promise<NotaFiscal>;
  aprovarManual: (id: string, observacao?: string) => Promise<NotaFiscal>;
  recusar: (id: string, motivo: string) => Promise<NotaFiscal>;
}

const NFContext = createContext<NFContextValue | undefined>(undefined);

export const NFProvider = ({ children }: { children: ReactNode }) => {
  const [versao, setVersao] = useState(0);
  const ultimoAviso = useRef(0);

  const refresh = useCallback(() => {
    ultimoAviso.current = Date.now();
    setVersao((v) => v + 1);
  }, []);

  useEffect(() => {
    const avisar = () => {
      if (Date.now() - ultimoAviso.current < INTERVALO_MIN_AVISO_MS) return;
      refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") avisar();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", avisar);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", avisar);
    };
  }, [refresh]);

  // As ações devolvem a NF atualizada; quem chamou atualiza a própria tela.
  const value = useMemo<NFContextValue>(
    () => ({
      versao,
      refresh,
      addNota: (formData) => api.postForm<NotaFiscal>("/nfs", formData),
      aprovar: (id) => api.patch<NotaFiscal>(`/nfs/${id}/aprovar`),
      aprovarManual: (id, observacao?) =>
        api.patch<NotaFiscal>(`/nfs/${id}/aprovar-manual`, observacao ? { observacao } : {}),
      recusar: (id, motivo) => api.patch<NotaFiscal>(`/nfs/${id}/recusar`, { motivo }),
    }),
    [versao, refresh],
  );

  return <NFContext.Provider value={value}>{children}</NFContext.Provider>;
};

export const useNF = () => {
  const ctx = useContext(NFContext);
  if (!ctx) throw new Error("useNF must be used within NFProvider");
  return ctx;
};
